import { useState, useEffect, useCallback, useRef, useMemo, Fragment, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import ItemForm, { type ItemFormData } from './components/ItemForm';
import ItemDetail from './components/ItemDetail';
import ItemReservationsModal from './components/ItemReservationsModal';
import VariantForm, { type VariantFormData } from './components/VariantForm';
import BulkVariantModal, { type BulkRow } from './components/BulkVariantModal';
import HsnVerifyModal from './components/HsnVerifyModal';
import { BarcodeLabelSheet } from '@/components/print/BarcodeLabel';
import { categoryService } from '@/services/categoryService';
import { useToast } from '@/contexts/ToastContext';
import { useDebounce } from '@/utils/debounce';
import { formatINR } from '@/utils/format';
import { MODULES } from '@/utils/permissions.js';
import { useAuth } from '@/contexts/AuthContext';
import {
  createItem,
  updateItem,
  deleteItem,
  filterItems,
  mapApiToItem,
  createBulkVariants,
  createSingleVariant,
  updateVariant,
  type ItemPayload,
  type FilterItemsParams,
} from '@/api/item.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { useBarcodeCartStore } from '@/stores/barcodeCartStore';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Item extends ItemFormData {
  id: string;
  stock: number;
  reservedQty?: number;
  availableQty?: number;
  brand: string;
  mrp?: number;
  articleNo: string;
  sizeColor?: string;
  warehouseId: string;
  isParent?: boolean;
  hasVariants?: boolean;
  variantCount?: number;
  isVariant?: boolean;
  parentItemId?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string> | null;
  variantSku?: string | null;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  bomId?: string | null;
  preferredSupplierName?: string | null;
  preferredSubcontractorName?: string | null;
  bomCode?: string | null;
  warehouseName?: string | null;
}

// ─── Table layout ─────────────────────────────────────────────────────────────
// Related item form fields share one stacked cell, so an item fits on screen without much
// sideways scrolling; clicking a row opens the item with every field. Column groups can be
// hidden by the viewer (remembered in this browser).

const COLUMN_GROUPS = [
  { key: 'tax', label: 'Tax' },
  { key: 'rates', label: 'Rates' },
  { key: 'stock', label: 'Stock' },
  { key: 'procurement', label: 'Procurement' },
  { key: 'production', label: 'BOM & Production' },
  { key: 'tracking', label: 'Tracking & QC' },
] as const;
type ColumnGroup = (typeof COLUMN_GROUPS)[number]['key'];
const HIDDEN_GROUPS_KEY = 'items.hiddenColumnGroups';

const PROCUREMENT_LABEL: Record<string, string> = { MAKE: 'Make', BUY: 'Buy', SUBCONTRACT: 'Subcontract' };
const rupees = (v?: number | null) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const Sub = ({ children }: { children: ReactNode }) => (
  <p className="text-xs text-[#94a3b8] leading-tight whitespace-nowrap">{children}</p>
);
const Chip = ({ children, cls }: { children: ReactNode; cls: string }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{children}</span>
);
const NONE = <span className="text-xs text-[#94a3b8]">—</span>;

const ITEM_TYPE_LABELS: Record<string, { label: string; cls: string }> = {
  RAW_MATERIAL: { label: 'Raw Material', cls: 'bg-orange-100 text-orange-700' },
  SEMI_FINISHED: { label: 'Semi-Finished', cls: 'bg-amber-100 text-amber-700' },
  FINISHED_GOOD: { label: 'Finished Good', cls: 'bg-green-100 text-green-700' },
  CONSUMABLE: { label: 'Consumable', cls: 'bg-blue-100 text-blue-700' },
  PACKAGING: { label: 'Packaging', cls: 'bg-gray-100 text-gray-700' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapFormToPayload(form: ItemFormData): ItemPayload {
  return {
    name: form.name,
    code: form.code || undefined,
    barcode: form.barcode || undefined,
    categoryId: form.categoryId || undefined,
    categoryName: form.categoryName || undefined,
    brand: form.brand || undefined,
    hsnCode: String(form.hsnCode).trim(),
    hsnDescription: form.hsnDescription ?? undefined,
    hsnVerified: form.hsnVerified ?? false,
    taxRate: form.taxRate,
    unitId: form.unitId || undefined,
    unitName: form.unitName || undefined,
    purchaseRate: form.purchaseRate,
    saleRate: form.saleRate,
    mrp: form.mrp,
    minStockLevel: form.minStockLevel,
    articleNo: form.articleNo || undefined,
    isActive: form.isActive,
    warehouseId: form.warehouseId,
    openingStock: form.openingStock ?? 0,
    itemType: form.itemType,
    procurementType: form.procurementType || undefined,
    standardCost: form.standardCost,
    isBatchTracked: form.isBatchTracked,
    isSerialTracked: form.isSerialTracked,
    hasExpiry: form.hasExpiry,
    shelfLifeDays: form.shelfLifeDays ?? undefined,
    bomId: form.bomId || undefined,
    bomVersion: form.bomVersion || undefined,
    reorderPoint: form.reorderPoint,
    reorderQty: form.reorderQty,
    leadTimeDays: form.leadTimeDays,
    preferredSupplierId: form.preferredSupplierId || undefined,
    preferredSubcontractorId: form.preferredSubcontractorId || null,
    jobWorkRate: form.jobWorkRate ?? 0,
    scrapRate: form.scrapRate ?? 0,
    itemGroup: form.itemGroup || undefined,
    drawingNumber: form.drawingNumber || undefined,
    specifications: form.specifications || undefined,
    requiresIncomingQC: form.requiresIncomingQC,
    requiresFinalQC: form.requiresFinalQC,
    isParent: form.isParent,
    hasVariants: form.hasVariants,
  };
}

// ─── Stock status helpers ─────────────────────────────────────────────────────

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';

function getStockStatus(item: Item): StockStatus {
  const stock = Number(item.stock ?? 0);
  const min = Number(item.minStockLevel ?? 0);
  if (stock === 0) return 'OUT_OF_STOCK';
  if (stock < min) return 'LOW_STOCK';
  return 'IN_STOCK';
}

function stockBadge(item: Item) {
  const s = getStockStatus(item);
  if (s === 'IN_STOCK') return { cls: 'bg-green-100 text-green-700', label: 'In Stock' };
  if (s === 'LOW_STOCK') return { cls: 'bg-amber-100 text-amber-700', label: 'Low Stock' };
  return { cls: 'bg-red-100 text-red-600', label: 'Out of Stock' };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ItemsPage() {
  const navigate = useNavigate();
  const { addToCart, setItems: setCartItems } = useBarcodeCartStore();
  const { success, error: toastError } = useToast();
  const { selectedWarehouseId } = useWarehouseStore();

  const { hasPermission } = useAuth();
  const canCreateItem = hasPermission(MODULES.ITEMS, 'create');
  const canEditItem = hasPermission(MODULES.ITEMS, 'edit');
  const canDeleteItem = hasPermission(MODULES.ITEMS, 'delete');

  // ── Data & loading ──
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  // ── Search & filters ──
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [filterCat, setFilterCat] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | StockStatus | 'RESERVED'>('ALL');
  const [filterActive, setFilterActive] = useState<'ALL' | 'true' | 'false'>('ALL');
  const [isSaving, setIsSaving] = useState(false);

  // ── Drawer / modal state ──
  const [slideOpen, setSlideOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Item | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [reservationsModalItem, setReservationsModalItem] = useState<{ id: string; name: string; code?: string } | null>(null);

  // ── Variant State ──
  const [showVariants, setShowVariants] = useState(true);
  // Column groups the viewer hid (all shown by default)
  const [hiddenGroups, setHiddenGroups] = useState<Set<ColumnGroup>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(HIDDEN_GROUPS_KEY) || '[]'));
    } catch {
      return new Set();
    }
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const show = (g: ColumnGroup) => !hiddenGroups.has(g);
  const toggleGroup = (g: ColumnGroup) => {
    setHiddenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      try { localStorage.setItem(HIDDEN_GROUPS_KEY, JSON.stringify([...next])); } catch { /* not saved */ }
      return next;
    });
  };
  // Item, Type, Unit, Active and Actions are always shown
  const columnCount = 5 + COLUMN_GROUPS.filter((g) => show(g.key)).length + (show('stock') ? 1 : 0);

  /** The optional column groups of one item or variant row, each a stacked cell. */
  const renderGroupCells = (it: Item, isVariant: boolean) => {
    const badge = stockBadge(it);
    const available = it.availableQty ?? Math.max(0, (it.stock || 0) - (it.reservedQty || 0));
    const cell = `px-3 ${isVariant ? 'py-2' : 'py-2.5'} align-top`;
    const planning = [
      it.leadTimeDays ? `Lead ${it.leadTimeDays}d` : null,
      it.reorderPoint ? `Reorder at ${it.reorderPoint}` : null,
      it.reorderQty ? `qty ${it.reorderQty}` : null,
    ].filter(Boolean);
    const tracking = [
      it.isBatchTracked && <Chip key="b" cls="bg-amber-50 text-amber-600">Batch</Chip>,
      it.isSerialTracked && <Chip key="s" cls="bg-blue-50 text-blue-600">Serial{it.serialPrefix ? ` ${it.serialPrefix}` : ''}</Chip>,
      it.isSerialTracked && it.defaultWarrantyMonths ? <Chip key="w" cls="bg-slate-100 text-slate-600">{it.defaultWarrantyMonths}m warranty</Chip> : null,
      it.hasExpiry && <Chip key="e" cls="bg-rose-50 text-rose-600">Expiry{it.shelfLifeDays ? ` ${it.shelfLifeDays}d` : ''}</Chip>,
      it.requiresIncomingQC && <Chip key="iq" cls="bg-emerald-50 text-emerald-700">Incoming QC</Chip>,
      it.requiresFinalQC && <Chip key="fq" cls="bg-emerald-50 text-emerald-700">Final QC</Chip>,
    ].filter(Boolean);
    const typeInfo = ITEM_TYPE_LABELS[it.itemType || ''] || { label: it.itemType || '—', cls: 'bg-gray-100 text-gray-700' };
    return (
      <>
        <td className={`${cell} whitespace-nowrap`}>
          <Chip cls={`${typeInfo.cls}${isVariant ? ' opacity-70' : ''}`}>{typeInfo.label}</Chip>
        </td>
        <td className={`${cell} whitespace-nowrap text-xs text-[#475569]`}>
          {it.unitName || (it.isParent ? '—' : (
            // Stock, issue and production quantities need a unit: flag it for fixing
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium" title="Edit the item and choose its unit">
              <i className="ri-error-warning-line" /> Unit missing
            </span>
          ))}
        </td>
        {show('tax') && (
          <td className={`${cell} whitespace-nowrap`}>
            <p className="font-mono text-xs text-[#475569]">{it.hsnCode || '—'}</p>
            <Chip cls="bg-indigo-50 text-[#4f46e5]">GST {it.taxRate}%</Chip>
          </td>
        )}
        {show('rates') && (
          <td className={`${cell} whitespace-nowrap text-right`}>
            <p className="font-semibold text-[#1e293b]">{formatINR(it.saleRate)}<span className="ml-1 text-xs font-normal text-[#94a3b8]">sale</span></p>
            <Sub>Purchase {formatINR(it.purchaseRate)}</Sub>
            {it.mrp ? <Sub>MRP {rupees(it.mrp)}</Sub> : null}
            {it.standardCost ? <Sub>Std cost {rupees(it.standardCost)}</Sub> : null}
          </td>
        )}
        {show('stock') && (
          <td className={`${cell} whitespace-nowrap text-right`}>
            <p>
              <span className={`font-bold ${it.stock <= it.minStockLevel ? 'text-amber-600' : 'text-[#1e293b]'}`}>{it.stock}</span>
              <span className="text-xs text-[#94a3b8]"> / min {it.minStockLevel || 0}</span>
            </p>
            {Number(it.reservedQty || 0) > 0 && (
              <p className="text-xs text-[#64748b]">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReservationsModalItem({ id: it.id, name: it.variantName || it.name, code: it.variantSku || it.code });
                    }}
                    className="inline-flex items-center gap-0.5 font-semibold text-amber-700 hover:underline cursor-pointer"
                    title="Click to view where this material is reserved (PO / Orders)"
                  >
                    <i className="ri-lock-line text-[10px]" />Reserved {it.reservedQty}
                  </button>
              </p>
            )}
            <span className={`inline-block mt-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
          </td>
        )}
        {show('stock') && (
          <td className={`${cell} whitespace-nowrap text-right`}>
            <span className={`font-bold ${available > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{available}</span>
          </td>
        )}
        {show('procurement') && (
          <td className={`${cell} min-w-[160px]`}>
            {it.procurementType ? <Chip cls="bg-slate-100 text-slate-700">{PROCUREMENT_LABEL[it.procurementType] || it.procurementType}</Chip> : NONE}
            {it.preferredSubcontractorName ? (
              <Sub>{it.preferredSubcontractorName}{it.jobWorkRate ? ` · ${rupees(it.jobWorkRate)}/unit` : ''}</Sub>
            ) : it.preferredSupplierName ? (
              <Sub>Supplier: {it.preferredSupplierName}</Sub>
            ) : null}
            {planning.length > 0 && <Sub>{planning.join(' · ')}</Sub>}
          </td>
        )}
        {show('production') && (
          <td className={`${cell} min-w-[140px]`}>
            {it.bomId ? (
              <p className="text-xs text-green-600 font-medium whitespace-nowrap">
                <i className="ri-checkbox-circle-fill" /> {it.bomCode ? `${it.bomCode}${it.bomVersion ? ` v${it.bomVersion}` : ''}` : 'BOM linked'}
              </p>
            ) : isVariant ? <Sub>BOM inherited</Sub> : NONE}
            {it.productionUnit && <Sub>Prod. unit: {it.productionUnit}</Sub>}
            {it.itemGroup && <Sub>Group: {it.itemGroup}</Sub>}
            {it.drawingNumber && <Sub>Drawing: {it.drawingNumber}</Sub>}
            {it.scrapRate ? <Sub>Scrap {rupees(it.scrapRate)}/unit</Sub> : null}
          </td>
        )}
        {show('tracking') && (
          <td className={cell}>
            {tracking.length ? <div className="flex flex-wrap gap-1 max-w-[180px]">{tracking}</div> : NONE}
          </td>
        )}
      </>
    );
  };
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [variantSlideOpen, setVariantSlideOpen] = useState(false);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const [editingVariant, setEditingVariant] = useState<Item | null>(null);

  // ── Bulk Variant State ──
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkModalParent, setBulkModalParent] = useState<Item | null>(null);
  const [labelSheetItems, setLabelSheetItems] = useState<Array<{ itemName: string; barcode: string; hsnCode?: string; rate?: number; unit?: string }>>([]);

  // ── HSN Verify State ──
  const [hsnVerifyOpen, setHsnVerifyOpen] = useState(false);

  const handlePrintSingleLabel = (item: Item) => {
    addToCart({
      itemId: item.id,
      name: item.name,
      brand: item.brand || '—',
      category: item.categoryName || '—',
      barcode: item.barcode || item.code,
      mrp: item.mrp || 0,
      saleRate: item.saleRate || 0,
      labelQty: 1,
      isSerialTracked: Boolean(item.isSerialTracked),
      isBatchTracked: Boolean(item.isBatchTracked),
    });
    navigate('/print/barcode-management?design=true');
  };

  const handlePrintSelectedLabels = () => {
    const itemsWithBarcodes = items.filter((i) => (i.barcode || i.code) && !i.isVariant).map((i) => ({
      itemId: i.id,
      name: i.name,
      brand: i.brand || '—',
      category: i.categoryName || '—',
      barcode: i.barcode || i.code,
      mrp: i.mrp || 0,
      saleRate: i.saleRate || 0,
      labelQty: 1,
      isSerialTracked: Boolean(i.isSerialTracked),
      isBatchTracked: Boolean(i.isBatchTracked),
    }));
    if (itemsWithBarcodes.length === 0) {
      toastError('No items with barcodes to print');
      return;
    }
    setCartItems(itemsWithBarcodes);
    navigate('/print/barcode-management?design=true');
  };

  const allSkus = useMemo(() => {
    const skus: string[] = [];
    items.forEach((i) => {
      if (i.code) skus.push(i.code);
      if (i.variantSku) skus.push(i.variantSku);
    });
    return skus;
  }, [items]);

  const existingVariantsForBulk = useMemo(() => {
    if (!bulkModalParent) return [];
    return items.filter((i) => i.isVariant && i.parentItemId === bulkModalParent.id);
  }, [items, bulkModalParent]);

  // Auto-expand parents when search matches their variants
  useEffect(() => {
    if (!search || !showVariants) return;
    const toExpand = new Set<string>();
    items.forEach((item) => {
      if (item.isVariant && item.parentItemId && (
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.code.toLowerCase().includes(search.toLowerCase()) ||
        (item.barcode && item.barcode.includes(search)) ||
        (item.variantName && item.variantName.toLowerCase().includes(search.toLowerCase())) ||
        (item.variantSku && item.variantSku.toLowerCase().includes(search.toLowerCase()))
      )) {
        toExpand.add(item.parentItemId);
      }
    });
    if (toExpand.size > 0) {
      setExpandedParents((prev) => new Set([...prev, ...toExpand]));
    }
  }, [search, items, showVariants]);

  // ── Fetch items ──
  const fetchItems = useCallback(
    async (params: FilterItemsParams = {}) => {
      setIsLoading(true);
      try {
        const finalParams: FilterItemsParams = {
          ...params,
          warehouseId: selectedWarehouseId || undefined,
        };
        const res = await filterItems(finalParams);
        if (res.success && res.data) {
          setItems(res.data.map((r) => mapApiToItem(r) as Item));
        }
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Failed to load items');
      } finally {
        setIsLoading(false);
      }
    },
    [toastError, selectedWarehouseId],
  );

  useEffect(() => {
    const params: FilterItemsParams = {};
    if (search.trim()) params.search = search.trim();
    if (filterCat !== 'ALL') params.categoryId = filterCat;
    if (filterStatus !== 'ALL' && filterStatus !== 'RESERVED') params.stockStatus = filterStatus;
    if (filterActive !== 'ALL') params.isActive = filterActive;
    void fetchItems(params);
  }, [
    search,
    filterCat,
    filterStatus,
    filterActive,
    selectedWarehouseId,
    fetchItems,
  ]);

  // Load categories once
  useEffect(() => {
    void (async () => {
      try {
        const list = await categoryService.list();
        setCategories(
          list
            .map((c) => ({ id: c.id, name: c.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch (err) {
        toastError(
          err instanceof Error ? err.message : 'Failed to load categories',
        );
      }
    })();
  }, [toastError]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    const matchingVariantParentIds = new Set<string>();
    if (q) {
      items.forEach((item) => {
        if (
          item.isVariant &&
          item.parentItemId &&
          (item.name.toLowerCase().includes(q) ||
            item.code.toLowerCase().includes(q) ||
            (item.barcode && item.barcode.includes(search)) ||
            (item.variantName && item.variantName.toLowerCase().includes(q)) ||
            (item.variantSku && item.variantSku.toLowerCase().includes(q)))
        ) {
          matchingVariantParentIds.add(item.parentItemId);
        }
      });
    }

    return items.filter((item) => {
      const selfMatch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        (item.barcode && item.barcode.includes(search));
      const variantMatch = matchingVariantParentIds.has(item.id);
      const matchSearch = selfMatch || variantMatch;
      const matchCat = filterCat === 'ALL' || item.categoryId === filterCat;
      const matchType = filterType === 'ALL' || item.itemType === filterType;
      const matchStatus =
        filterStatus === 'ALL'
          ? true
          : filterStatus === 'RESERVED'
          ? Number(item.reservedQty || 0) > 0
          : getStockStatus(item) === filterStatus;
      const matchActive = filterActive === 'ALL' || String(item.isActive) === filterActive;
      return matchSearch && matchCat && matchType && matchStatus && matchActive && !item.isVariant;
    });
  }, [items, search, filterCat, filterType, filterStatus, filterActive]);

  // ── Derived stats ──
  const stats = useMemo(
    () => ({
      total: items.length,
      active: items.filter((i) => i.isActive).length,
      lowStock: items.filter((i) => getStockStatus(i) === 'LOW_STOCK').length,
      outOfStock: items.filter((i) => getStockStatus(i) === 'OUT_OF_STOCK').length,
      reserved: items.filter((i) => Number(i.reservedQty || 0) > 0).length,
    }),
    [items],
  );

  // ── Drawer & Variant helpers ──
  const toggleExpand = (parentId: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  const openAdd = () => {
    setEditingItem(null);
    setSlideOpen(true);
  };

  const openEdit = (item: Item) => {
    if (item.isVariant) {
      setEditingVariant(item);
      setActiveParentId(item.parentItemId || null);
      setVariantSlideOpen(true);
    } else {
      setEditingItem(item);
      setSlideOpen(true);
    }
  };

  const openAddVariant = (parent: Item) => {
    setEditingVariant(null);
    setActiveParentId(parent.id);
    setVariantSlideOpen(true);
  };

  const openBulkVariants = (parent: Item) => {
    setBulkModalParent(parent);
    setBulkModalOpen(true);
  };

  const closeDrawer = () => setSlideOpen(false);
  const openDetails = (i: Item) => setSelectedItem(i);

  const handleSaveBulkVariants = async (bulkRows: BulkRow[], attributeNames: string[]) => {
    const parent = bulkModalParent;
    if (!parent) return;

    try {
      const res = await createBulkVariants(parent.id, {
        attributeNames,
        variants: bulkRows,
      });

      if (res.success) {
        success(`${bulkRows.length} variants created and saved successfully!`);
        await fetchItems();
        setExpandedParents((prev) => new Set([...prev, parent.id]));
      } else {
        toastError(res.message || 'Failed to save variants');
      }
    } catch (err: any) {
      toastError(err?.message || 'Error saving bulk variants to database');
    } finally {
      setBulkModalOpen(false);
      setBulkModalParent(null);
    }
  };

  const handleSaveVariant = async (data: VariantFormData) => {
    const parent = items.find((i) => i.id === activeParentId);
    if (!parent) return;

    try {
      if (editingVariant) {
        const res = await updateVariant(editingVariant.id, {
          variantName: data.variantName,
          sku: data.variantSku,
          code: data.code,
          saleRate: data.saleRate,
          purchaseRate: data.purchaseRate,
          mrp: data.mrp,
          isActive: data.isActive,
          attributes: data.variantAttributes,
        });
        if (res.success) {
          success('Variant updated successfully');
          await fetchItems();
        } else {
          toastError(res.message || 'Failed to update variant');
        }
      } else {
        const res = await createSingleVariant({
          parentItemId: parent.id,
          variantName: data.variantName,
          code: data.code,
          sku: data.variantSku,
          purchaseRate: data.purchaseRate,
          saleRate: data.saleRate,
          mrp: data.mrp,
          isActive: data.isActive,
          attributes: data.variantAttributes,
          warehouseId: parent.warehouseId || selectedWarehouseId || '',
          bomId: data.bomId || null,
        });
        if (res.success) {
          success('Variant added successfully');
          await fetchItems();
          setExpandedParents((prev) => new Set([...prev, parent.id]));
        } else {
          toastError(res.message || 'Failed to add variant');
        }
      }
    } catch (err: any) {
      toastError(err?.message || 'Failed to save variant');
    } finally {
      setVariantSlideOpen(false);
    }
  };

  // ── Save (create or update main item) ──
  const handleSave = async (data: ItemFormData) => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const payload = mapFormToPayload(data);

      if (editingItem) {
        const res = await updateItem(editingItem.id, payload);
        if (res.success && res.data) {
          const updated = {
            ...(mapApiToItem(res.data) as Item),
            stock: editingItem.stock,
          };
          setItems((prev) =>
            prev.map((i) => (i.id === editingItem.id ? updated : i)),
          );
          success('Item updated successfully');
          closeDrawer();
        }
      } else {
        const res = await createItem(payload);
        if (res.success && res.data) {
          const created = {
            ...(mapApiToItem(res.data) as Item),
            stock: payload.openingStock ?? 0,
          };
          setItems((prev) => [created, ...prev]);
          success('Item added successfully');
          closeDrawer();
        }
      }
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Soft delete ──
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      if (!deleteConfirm.isVariant) {
        await deleteItem(deleteConfirm.id);
      }
      setItems((prev) => {
        let next = prev.filter((i) => i.id !== deleteConfirm.id);
        if (deleteConfirm.isVariant && deleteConfirm.parentItemId) {
          const parent = next.find((i) => i.id === deleteConfirm.parentItemId);
          if (parent) {
            const newCount = Math.max(0, (parent.variantCount || 0) - 1);
            next = next.map((i) =>
              i.id === deleteConfirm.parentItemId
                ? { ...i, variantCount: newCount, hasVariants: newCount > 0, isParent: newCount > 0 }
                : i
            );
          }
        }
        return next;
      });
      success(deleteConfirm.isVariant ? 'Variant deactivated' : 'Item deactivated (soft delete)');
      setDeleteConfirm(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to deactivate item',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (item: Item) => {
    setItems((prev) =>
      prev.map((c) => (c.id === item.id ? { ...c, isActive: !c.isActive } : c)),
    );
    try {
      await updateItem(item.id, { isActive: !item.isActive });
      success(`${item.name} ${item.isActive ? 'deactivated' : 'activated'}`);
    } catch (err) {
      setItems((prev) =>
        prev.map((c) =>
          c.id === item.id ? { ...c, isActive: item.isActive } : c,
        ),
      );
      toastError(
        err instanceof Error ? err.message : 'Failed to update status',
      );
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-[#1e293b]">Item Master</h2>
              <p className="text-sm text-[#64748b] mt-0.5">
                Products, goods and inventory items
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHsnVerifyOpen(true)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg bg-white text-[#4f46e5] text-sm font-semibold border border-[#c7d2fe] hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-barcode-box-line" /> Verify HSN
            </button>
            {canCreateItem && (
              <button
                onClick={openAdd}
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" /> Add Item
              </button>
            )}
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(
            [
              {
                label: 'Total Items',
                value: stats.total,
                icon: 'ri-box-3-line',
                color: 'indigo',
              },
              {
                label: 'Active Items',
                value: stats.active,
                icon: 'ri-checkbox-circle-line',
                color: 'green',
              },
              {
                label: 'Reserved Items',
                value: stats.reserved,
                icon: 'ri-lock-line',
                color: 'purple',
              },
              {
                label: 'Low Stock',
                value: stats.lowStock,
                icon: 'ri-alert-line',
                color: 'amber',
              },
              {
                label: 'Out of Stock',
                value: stats.outOfStock,
                icon: 'ri-error-warning-line',
                color: 'red',
              },
            ] as const
          ).map((c) => (
            <div
              key={c.label}
              className={`bg-white border rounded-xl p-3.5 flex items-center gap-3 ${
                c.color === 'red' && c.value > 0
                  ? 'border-red-200'
                  : c.color === 'purple' && c.value > 0
                  ? 'border-amber-200'
                  : 'border-[#e2e8f0]'
              }`}
            >
              <div
                className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${
                  c.color === 'indigo'
                    ? 'bg-indigo-50'
                    : c.color === 'green'
                      ? 'bg-green-50'
                      : c.color === 'purple'
                        ? 'bg-amber-50'
                        : c.color === 'amber'
                          ? 'bg-amber-50'
                          : 'bg-red-50'
                }`}
              >
                <i
                  className={`${c.icon} text-lg ${
                    c.color === 'indigo'
                      ? 'text-[#4f46e5]'
                      : c.color === 'green'
                        ? 'text-green-600'
                        : c.color === 'purple'
                          ? 'text-amber-700'
                          : c.color === 'amber'
                            ? 'text-amber-600'
                            : 'text-red-500'
                  }`}
                />
              </div>
              <div className="min-w-0">
                <p
                  className={`text-xl font-bold ${
                    c.color === 'red' && c.value > 0
                      ? 'text-red-600'
                      : c.color === 'purple' && c.value > 0
                      ? 'text-amber-800'
                      : 'text-[#1e293b]'
                  }`}
                >
                  {c.value}
                </p>
                <p className="text-[11px] font-medium text-[#64748b] truncate">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Warehouse indicator banner ── */}
        {selectedWarehouseId && (
          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-700 font-medium">
            <i className="ri-store-3-line text-indigo-500" />
            Showing items for warehouse selected in the top bar. To see all
            warehouses, switch the selection above.
          </div>
        )}

        {/* ── Filters ── */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, code or barcode..."
              className="w-full h-9 pl-9 pr-4 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
            />
          </div>

          {/* Category */}
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#64748b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer"
          >
            <option value="ALL">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Item Type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#64748b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer"
          >
            <option value="ALL">All Types</option>
            <option value="RAW_MATERIAL">Raw Material</option>
            <option value="SEMI_FINISHED">Semi-Finished</option>
            <option value="FINISHED_GOOD">Finished Good</option>
            <option value="CONSUMABLE">Consumable</option>
            <option value="PACKAGING">Packaging</option>
          </select>

          {/* Stock status pills */}
          <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1">
            {(['ALL', 'IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK', 'RESERVED'] as const).map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                    filterStatus === s
                      ? 'bg-white text-[#1e293b] shadow-sm'
                      : 'text-[#64748b] hover:text-[#1e293b]'
                  }`}
                >
                  {s === 'RESERVED' && <i className="ri-lock-line text-[11px] text-amber-600" />}
                  {s === 'ALL'
                    ? 'All'
                    : s === 'IN_STOCK'
                      ? 'In Stock'
                      : s === 'LOW_STOCK'
                        ? 'Low'
                        : s === 'OUT_OF_STOCK'
                          ? 'Out'
                          : `Reserved (${stats.reserved})`}
                </button>
              ),
            )}
          </div>

          {/* Active / inactive pills */}
          <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1">
            {(
              [
                { key: 'ALL', label: 'All Items' },
                { key: 'true', label: 'Active' },
                { key: 'false', label: 'Inactive' },
              ] as const
            ).map((s) => (
              <button
                key={s.key}
                onClick={() => setFilterActive(s.key)}
                className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filterActive === s.key
                    ? 'bg-white text-[#1e293b] shadow-sm'
                    : 'text-[#64748b] hover:text-[#1e293b]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Variant toggle button */}
          <button
            onClick={() => setShowVariants((v) => !v)}
            className={`h-9 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 border ${
              showVariants
                ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#4f46e5]'
            }`}
          >
            <i className={showVariants ? 'ri-eye-line' : 'ri-eye-off-line'} />
            {showVariants ? 'Variants ON' : 'Variants OFF'}
          </button>

          {/* Columns picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColumnMenu((v) => !v)}
              className="h-9 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 border bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#4f46e5]"
            >
              <i className="ri-layout-column-line" /> Columns
            </button>
            {showColumnMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowColumnMenu(false)} />
                <div className="absolute right-0 mt-1 z-40 w-56 bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-[#1e293b] mb-1">Show columns</p>
                  {COLUMN_GROUPS.map((g) => (
                    <label key={g.key} className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer">
                      <input type="checkbox" checked={show(g.key)} onChange={() => toggleGroup(g.key)} className="accent-[#4f46e5]" />
                      {g.label}
                    </label>
                  ))}
                  <p className="text-[11px] text-[#94a3b8] pt-1 border-t border-[#f1f5f9]">Click a row to see every field of the item.</p>
                </div>
              </>
            )}
          </div>

          {/* Print all labels */}
          <button
            onClick={handlePrintSelectedLabels}
            className="h-9 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
          >
            <i className="ri-barcode-line" /> Print All Labels
          </button>
        </div>

        {/* ── Table ── */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  {[
                    { key: 'item', label: 'Item', align: 'text-left' },
                    { key: 'type', label: 'Type', align: 'text-left' },
                    { key: 'unit', label: 'Unit', align: 'text-left' },
                    ...COLUMN_GROUPS.filter((g) => show(g.key)).flatMap((g) => [
                      {
                        key: g.key,
                        label: g.label,
                        align: g.key === 'rates' || g.key === 'stock' ? 'text-right' : 'text-left',
                      },
                      // Available stock has its own column next to Stock
                      ...(g.key === 'stock' ? [{ key: 'available', label: 'Available', align: 'text-right' }] : []),
                    ]),
                    { key: 'active', label: 'Active', align: 'text-center' },
                    { key: 'actions', label: 'Actions', align: 'text-right' },
                  ].map((h) => (
                    <th
                      key={h.key}
                      className={`${h.align} px-3 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap`}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* Loading skeleton */}
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b border-[#f1f5f9]">
                      {Array.from({ length: columnCount }).map((_, j) => (
                        <td key={j} className="px-3 py-3">
                          <div className="h-4 bg-[#f1f5f9] rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {/* Data rows */}
                {!isLoading &&
                  filtered.map((item, idx) => {
                    const isParent = Boolean(item.isParent || (item.variantCount && item.variantCount > 0));
                    const isExpanded = expandedParents.has(item.id);
                    const variantChildren = items.filter((i) => i.isVariant && i.parentItemId === item.id);
                    const extras = [item.brand, item.articleNo && `Art. ${item.articleNo}`, item.sizeColor, item.warehouseName].filter(Boolean);

                    return (
                      <Fragment key={item.id}>
                        {/* Parent / Main Item Row */}
                        <tr
                          onClick={() => openDetails(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openDetails(item);
                            }
                          }}
                          className={`border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors cursor-pointer ${
                            isParent ? 'bg-slate-50' : idx % 2 !== 0 ? 'bg-[#f8fafc]/40' : ''
                          }`}
                        >
                          {/* Item: name, code / barcode, type, category, brand … */}
                          <td className="px-3 py-2.5 align-top min-w-[220px] max-w-[300px]">
                            <div className="flex items-start gap-2">
                              {isParent && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(item.id);
                                  }}
                                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer shrink-0"
                                >
                                  <i className={isExpanded ? 'ri-arrow-down-s-line text-sm' : 'ri-arrow-right-s-line text-sm'} />
                                </button>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-[#1e293b] leading-tight">{item.name}</p>
                                <p className="font-mono text-xs text-[#94a3b8] leading-tight truncate">
                                  {item.code || '—'}
                                  {item.barcode && item.barcode !== item.code ? ` · ${item.barcode}` : ''}
                                </p>
                                <div className="flex flex-wrap items-center gap-1 leading-tight">
                                  {isParent && (
                                    <Chip cls={item.variantCount ? 'bg-indigo-50 text-[#4f46e5]' : 'bg-gray-100 text-gray-500'}>
                                      {item.variantCount || 0} variants
                                    </Chip>
                                  )}
                                  {item.categoryName && <span className="text-xs text-[#64748b]">{item.categoryName}</span>}
                                </div>
                                {extras.length > 0 && (
                                  <p className="text-xs text-[#94a3b8] leading-tight truncate" title={extras.join(' · ')}>{extras.join(' · ')}</p>
                                )}
                              </div>
                            </div>
                          </td>

                          {renderGroupCells(item, false)}

                          {/* Active toggle */}
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {canEditItem ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleToggleActive(item);
                                }}
                                className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4f46e5]/40 ${
                                  item.isActive ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
                                }`}
                                title={
                                  item.isActive
                                    ? 'Click to deactivate'
                                    : 'Click to activate'
                                }
                                aria-label={
                                  item.isActive
                                    ? 'Deactivate item'
                                    : 'Activate item'
                                }
                              >
                                <span
                                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center ${
                                    item.isActive
                                      ? 'translate-x-5'
                                      : 'translate-x-0'
                                  }`}
                                >
                                  <i
                                    className={`text-[8px] transition-all duration-300 ${
                                      item.isActive
                                        ? 'ri-check-line text-[#4f46e5]'
                                        : 'ri-close-line text-[#94a3b8]'
                                    }`}
                                  />
                                </span>
                              </button>
                            ) : (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                  item.isActive
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-slate-100 text-slate-500 border-slate-200'
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    item.isActive ? 'bg-green-500' : 'bg-slate-400'
                                  }`}
                                />
                                {item.isActive ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {isParent && canEditItem && (
                                <>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAddVariant(item);
                                    }}
                                    title="Add Variant"
                                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                                  >
                                    <i className="ri-add-line text-sm" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openBulkVariants(item);
                                    }}
                                    title="Add Multiple Variants"
                                    className="h-7 px-2 rounded-lg border border-[#4f46e5] text-[#4f46e5] hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap text-xs font-medium flex items-center gap-1"
                                  >
                                    <i className="ri-stack-line" /> Bulk
                                  </button>
                                </>
                              )}
                              {canEditItem && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(item);
                                  }}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                                  title="Edit"
                                >
                                  <i className="ri-edit-line text-sm" />
                                </button>
                              )}
                              {canDeleteItem && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(item);
                                  }}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                                  title="Deactivate"
                                >
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              )}
                              {(item.barcode || item.code) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrintSingleLabel(item);
                                  }}
                                  title="Print Label"
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 text-[#64748b] hover:text-amber-600 transition-colors cursor-pointer"
                                >
                                  <i className="ri-barcode-line text-sm" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Variant Child Rows */}
                        {isParent &&
                          isExpanded &&
                          showVariants &&
                          variantChildren.map((variant) => (
                              <tr
                                key={variant.id}
                                className="border-b border-[#f1f5f9] bg-[#f8fafc]/40"
                              >
                                <td className="px-3 py-2 align-top pl-8 min-w-[220px] max-w-[300px]">
                                  <div className="flex items-start gap-1">
                                    <span className="text-[#94a3b8] text-xs">└─</span>
                                    <div className="min-w-0">
                                      <p className="font-medium text-[#1e293b] text-sm leading-tight">{variant.variantName || variant.name}</p>
                                      <p className="font-mono text-xs text-[#94a3b8] leading-tight truncate">{variant.variantSku || variant.code}</p>
                                      {variant.variantAttributes && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {Object.entries(variant.variantAttributes).map(([k, v]) => (
                                            <Chip key={k} cls="bg-gray-100 text-gray-600">{k}: {v}</Chip>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                {renderGroupCells(variant, true)}
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className="text-xs text-gray-400">
                                    {variant.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    {canEditItem && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEdit(variant);
                                        }}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                                        title="Edit Variant"
                                      >
                                        <i className="ri-edit-line text-sm" />
                                      </button>
                                    )}
                                    {canDeleteItem && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteConfirm(variant);
                                        }}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                                        title="Delete Variant"
                                      >
                                        <i className="ri-delete-bin-line text-sm" />
                                      </button>
                                    )}
                                    {(variant.barcode || variant.code) && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePrintSingleLabel(variant);
                                        }}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 text-[#64748b] hover:text-amber-600 transition-colors cursor-pointer"
                                        title="Print Label"
                                      >
                                        <i className="ri-barcode-line text-sm" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                          ))}
                      </Fragment>
                    );
                  })}

                {/* Empty state */}
                {!isLoading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-12 text-center">
                      <i className="ri-box-3-line text-4xl text-[#e2e8f0] block mb-2" />
                      <p className="text-[#94a3b8] text-sm">
                        No items match your search
                      </p>
                      {searchInput && (
                        <p className="text-xs text-[#94a3b8] mt-1">
                          Try a different search term
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-[#e2e8f0]">
            <p className="text-xs text-[#94a3b8]">
              Showing {filtered.length} of {items.length} item{items.length !== 1 ? 's' : ''}
              {selectedWarehouseId ? ' for selected warehouse' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Slide-over form */}
      <ItemForm
        open={slideOpen}
        isEditing={!!editingItem}
        initialData={
          editingItem
            ? { ...editingItem, openingStock: editingItem.stock }
            : undefined
        }
        onClose={closeDrawer}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <VariantForm
        open={variantSlideOpen}
        parentItem={items.find((i) => i.id === activeParentId) || null}
        editingVariant={editingVariant}
        onClose={() => {
          setVariantSlideOpen(false);
          setEditingVariant(null);
          setActiveParentId(null);
        }}
        onSave={handleSaveVariant}
      />

      <BulkVariantModal
        open={bulkModalOpen}
        parentItem={bulkModalParent}
        existingVariants={existingVariantsForBulk}
        allSkus={allSkus}
        onClose={() => {
          setBulkModalOpen(false);
          setBulkModalParent(null);
        }}
        onSave={handleSaveBulkVariants}
      />

      {hsnVerifyOpen && <HsnVerifyModal onClose={() => setHsnVerifyOpen(false)} />}

      {/* Soft-delete confirm */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title={deleteConfirm?.isVariant ? 'Deactivate Variant' : 'Deactivate Item'}
        message={
          deleteConfirm?.isVariant
            ? `Deactivate variant "${deleteConfirm?.variantName || deleteConfirm?.name}"? It will be removed from this product.`
            : `Deactivate "${deleteConfirm?.name}"? It will be hidden from billing but history is preserved.`
        }
        variant="warning"
        confirmLabel={isDeleting ? 'Deactivating...' : 'Yes, Deactivate (Y)'}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteConfirm(null)}
      />

      {labelSheetItems.length > 0 && (
        <BarcodeLabelSheet
          labels={labelSheetItems}
          onClose={() => setLabelSheetItems([])}
        />
      )}

      {/* Floating Item Info Card */}
      <ItemDetail
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onOpenReservations={(it) => setReservationsModalItem({ id: it.id, name: it.name, code: it.code })}
      />

      {/* Item Reservations Breakdown Modal */}
      {reservationsModalItem && (
        <ItemReservationsModal
          itemId={reservationsModalItem.id}
          itemName={reservationsModalItem.name}
          itemCode={reservationsModalItem.code}
          onClose={() => setReservationsModalItem(null)}
        />
      )}
    </AppLayout>
  );
}